import React, { useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Divider,
  LinearProgress, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';

export default function TrainingCompliance() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const loadSummary = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const response = await fetch(API_ENDPOINTS.TRAINING_COMPLIANCE_SUMMARY, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Unable to load training progress');
      const data = await response.json();
      setSummary(data || []);
    } catch (error) {
      setErrorMsg(error.message || 'Unable to load training progress');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const sendReminders = async () => {
    setSending(true);
    setMessage('');
    try {
      const response = await fetch(API_ENDPOINTS.TRAINING_COMPLIANCE_REMINDERS, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Unable to send reminders');
      setMessage(
        t('training_compliance_reminders_sent', { count: data.queued || 0 }) ||
        `${data.queued || 0} reminder(s) queued.`
      );
    } catch (error) {
      setErrorMsg(error.message || 'Unable to send reminders');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('training_compliance_title') || 'Training Compliance'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('training_compliance_description') || 'Review assigned learning modules and progress. This view is informational and does not determine legal compliance.'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<MailOutlineIcon />}
            onClick={sendReminders}
            disabled={sending || loading}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {sending ? t('training_compliance_sending') || 'Sending...' : t('training_compliance_send_reminders') || 'Send reminders'}
          </Button>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light' }}>
            <AssignmentIcon />
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            {t('training_compliance_note') || 'Progress is sourced from each employee’s learning-module record.'}
          </Typography>
        </Box>
      </Paper>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('training_compliance_loading') || 'Loading training progress...'}
          </Typography>
        </Box>
      ) : summary.length === 0 ? (
        <Alert severity="info">
          {t('training_compliance_empty') || 'No authorized team members with learning-module records were found.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('name') || 'Employee'}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('assigned') || 'Assigned'}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('passed') || 'Passed'}</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('training_compliance_progress') || 'Progress'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.map((employee) => (
                <TableRow key={employee.email} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>
                        {(employee.fullname || employee.email || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{employee.fullname || employee.email}</Typography>
                        <Typography variant="caption" color="text.secondary">{employee.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={employee.assigned_modules} size="small" /></TableCell>
                  <TableCell><Chip label={employee.passed_modules} size="small" color="success" /></TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={employee.completion_pct}
                        sx={{ flex: 1, height: 6, borderRadius: 4 }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 38, textAlign: 'right' }}>
                        {employee.completion_pct}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
